/**
 * Warden — Deployment practice.
 *
 * Cerberus ships as an MIT core plus a commercial enterprise runtime, so these
 * pages describe getting it into production rather than selling a trial. Any
 * self-hosted claim has to stay true to what the runtime actually supports.
 */

import type { ServicePageContent } from "./types";

const PRACTICE = "Deployment";

const CERBERUS_DEPLOYMENT: ServicePageContent = {
  path: "/warden/cerberus-deployment",
  practice: PRACTICE,
  title: "Cerberus Runtime Deployment",
  summary:
    "Get the runtime enforcing real policy in production, without breaking the agents.",
  headline: "A runtime control is only worth having once it is in the call path",
  lede:
    "An engagement that takes Cerberus from evaluation to production: integrated with your agent frameworks, tuned against your traffic, and enforcing policy your teams understand and can change.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=cerberus-deployment" },
    { label: "Explore Cerberus", href: "/cerberus" },
  ],
  scope: {
    heading: "The hard part is the first block, not the first install",
    body:
      "Getting a runtime into the call path is an afternoon. Getting the organization comfortable with it refusing an action in production is the engagement, and it depends on running in observation long enough to know what enforcement would have done.",
    points: [
      {
        title: "Observation has to come first",
        body: "Enforcing on day one against traffic you have never characterized is how a security control gets switched off permanently.",
      },
      {
        title: "Policy has to be owned by someone",
        body: "A rule set only the vendor understands does not survive the first false positive at 5pm.",
      },
      {
        title: "Failure mode is a decision",
        body: "Fail-closed protects the data and can stop the business; that trade-off needs a named owner, not a default.",
      },
    ],
  },
  method: {
    heading: "How the deployment runs",
    steps: [
      {
        title: "Architecture and placement",
        body: "Where the runtime sits relative to your agents, frameworks and tool layer, including self-hosted deployment inside your own boundary where required.",
      },
      {
        title: "Observe",
        body: "Run in observation against production traffic, characterizing what would have been blocked and why.",
      },
      {
        title: "Tune",
        body: "Reduce false positives against real traffic and agree the failure mode per guarded action.",
      },
      {
        title: "Enforce and hand over",
        body: "Move to enforcement in stages, with your team owning the policy and the runbook by the end.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "Cerberus deployed in your environment, self-hosted where the boundary requires it",
      "An observation report characterizing what enforcement would have done to real traffic",
      "A tuned policy set owned and editable by your team",
      "A documented failure-mode decision per guarded action",
      "Runbooks, alerting integration and a handover your on-call team has walked through",
    ],
  },
  products: {
    heading: "What is deployed",
    slugs: ["cerberus", "tracelock"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Enablement & Training", href: "/warden/enablement" },
    ],
  },
  close: {
    heading: "In the call path, tuned, and owned by your team",
    body:
      "A runtime control in observation forever is a monitoring tool. The engagement ends when it is enforcing.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=cerberus-deployment" },
      { label: "Explore Cerberus", href: "/cerberus" },
    ],
  },
};

const IMPLEMENTATION: ServicePageContent = {
  path: "/warden/implementation",
  practice: PRACTICE,
  title: "Implementation & Migration",
  summary:
    "Stand up the platform and move off what you are replacing, without a gap in coverage.",
  headline: "Migrations fail on the data and the cutover, not on the product",
  lede:
    "Implementation of the Odingard platform, including migration from whatever you are replacing — control sets, historical evidence and open items — with continuous coverage across the cutover.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=implementation" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "The incumbent holds history you still need",
    body:
      "Replacing a compliance platform means moving control definitions, evidence with dates attached and work in flight — and an auditor will ask about the period that spans both systems.",
    points: [
      {
        title: "Evidence has to keep its dates",
        body: "Migrated artifacts that lose their original timestamps are substantially less useful at audit.",
      },
      {
        title: "Coverage cannot lapse",
        body: "A monitoring gap during cutover becomes an exception you have to explain.",
      },
      {
        title: "Adoption decides the outcome",
        body: "A migrated platform nobody uses is a more expensive version of the problem you started with.",
      },
    ],
  },
  method: {
    heading: "How the implementation runs",
    steps: [
      {
        title: "Discovery and plan",
        body: "Current state, data to migrate, integrations required and a cutover plan with a rollback position.",
      },
      {
        title: "Configure",
        body: "Tenancy, roles, control library, connectors and integrations against your environment.",
      },
      {
        title: "Migrate",
        body: "Move controls, evidence and open items with original dates and attribution preserved.",
      },
      {
        title: "Parallel run and cutover",
        body: "Both systems live until posture matches, then cutover with coverage documented across the boundary.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "A configured production tenant with roles and integrations in place",
      "Migrated controls and evidence with original dates and attribution preserved",
      "A parallel-run reconciliation showing posture matched before cutover",
      "A documented coverage statement spanning the migration period",
      "Trained administrators and a support path",
    ],
  },
  products: {
    heading: "What is implemented",
    slugs: ["tracelock", "cerberus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Integration Engineering", href: "/warden/integration" },
      { label: "Enablement & Training", href: "/warden/enablement" },
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
    ],
  },
  close: {
    heading: "No gap in the record",
    body:
      "The measure of a good migration is that the audit after it never has to discuss the migration.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=implementation" },
      { label: "Explore TraceLock", href: "/tracelock" },
    ],
  },
};

const INTEGRATION: ServicePageContent = {
  path: "/warden/integration",
  practice: PRACTICE,
  title: "Integration Engineering",
  summary:
    "Connect the platform to the systems that hold your evidence, including the bespoke ones.",
  headline: "Your evidence lives in systems no vendor has a connector for",
  lede:
    "Engineering work to connect Odingard to your estate — cloud accounts, identity, source control and the internal systems that only you run — so control monitoring covers what actually matters.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=integration" },
    { label: "Explore TraceLock", href: "/tracelock" },
  ],
  scope: {
    heading: "Standard connectors cover the standard estate",
    body:
      "AWS, GCP, GitHub and Cloudflare are supported out of the box. The controls that fail an audit are usually the ones monitored through an internal deployment system or a mainframe-adjacent process with no public API.",
    points: [
      {
        title: "The critical system is often the custom one",
        body: "The internal change management tool is exactly the control an auditor samples.",
      },
      {
        title: "Read-only access takes negotiation",
        body: "Getting appropriately scoped credentials across account boundaries is organisational work as much as technical.",
      },
      {
        title: "An unmaintained integration is a silent failure",
        body: "A connector that stops returning data looks identical to a control that is passing.",
      },
    ],
  },
  method: {
    heading: "How the engagement runs",
    steps: [
      {
        title: "Integration inventory",
        body: "Which controls need which systems, and which of those have a supported connector already.",
      },
      {
        title: "Access design",
        body: "Least-privilege read-only credentials, scoped per account, with a rotation plan.",
      },
      {
        title: "Build",
        body: "Custom collectors for the systems with no supported connector, written against your interfaces.",
      },
      {
        title: "Monitor the monitors",
        body: "Staleness detection so a broken collector raises an alert rather than reporting a pass.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "Supported connectors configured across AWS, GCP, GitHub and Cloudflare as applicable",
      "Custom collectors for internal systems, with source and documentation handed over",
      "Least-privilege credential design with a rotation plan",
      "Staleness detection so a failed collector cannot masquerade as a passing control",
      "An integration runbook your platform team can maintain",
    ],
  },
  products: {
    heading: "What is integrated",
    slugs: ["tracelock", "cerberus"],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Implementation & Migration", href: "/warden/implementation" },
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
    ],
  },
  close: {
    heading: "Coverage where it counts",
    body:
      "A monitoring program that only covers the systems with easy APIs will be sampled precisely where it does not.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=integration" },
      { label: "Explore TraceLock", href: "/tracelock" },
    ],
  },
};

const ENABLEMENT: ServicePageContent = {
  path: "/warden/enablement",
  practice: PRACTICE,
  title: "Enablement & Training",
  summary:
    "Leave your teams able to run this without us, which is the point.",
  headline: "The engagement should end with your team not needing us",
  lede:
    "Role-specific training and enablement for the people who will operate what we deployed — engineers writing policy, compliance staff running the program, and the executives who have to answer for both.",
  ctas: [
    { label: "Request a briefing", href: "/contact?topic=enablement" },
    { label: "Explore the practices", href: "/warden" },
  ],
  scope: {
    heading: "Generic AI training does not change behavior",
    body:
      "A course on AI risk delivered to everyone produces awareness and no change in practice. The teams shipping agents need something specific to the systems they are building and the controls they now have to work inside.",
    points: [
      {
        title: "Engineers need the failure modes",
        body: "Injection, tool abuse and exfiltration paths, demonstrated against systems that resemble theirs.",
      },
      {
        title: "Compliance staff need the platform",
        body: "How to operate control monitoring and evidence day to day, not a product overview.",
      },
      {
        title: "Executives need the questions",
        body: "What to ask a team proposing an agent, and what a good answer sounds like.",
      },
    ],
  },
  method: {
    heading: "How enablement runs",
    steps: [
      {
        title: "Audience mapping",
        body: "Who needs what, at what depth, and what they should be able to do afterwards.",
      },
      {
        title: "Tailor the material",
        body: "Built around your architecture and your policy set rather than a generic curriculum.",
      },
      {
        title: "Deliver",
        body: "Workshops for engineers, operational training for compliance staff, briefings for leadership.",
      },
      {
        title: "Leave the materials",
        body: "You keep the content and can run it again for new joiners without us.",
      },
    ],
  },
  deliverables: {
    heading: "What you hold at the end",
    items: [
      "Role-specific training built on your architecture and policy set",
      "Hands-on engineering workshops covering agent failure modes and the controls in place",
      "Operational training for the compliance team on the deployed platform",
      "A leadership briefing on what to ask before approving an agent",
      "The materials, yours to re-run for new joiners",
    ],
  },
  related: {
    heading: "Often bought with",
    links: [
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
      { label: "Implementation & Migration", href: "/warden/implementation" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
    ],
  },
  close: {
    heading: "Capability, not dependency",
    body:
      "We would rather be brought back for the next problem than retained to operate the last one.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=enablement" },
      { label: "Explore the practices", href: "/warden" },
    ],
  },
};

export const DEPLOYMENT_SERVICES: ServicePageContent[] = [
  CERBERUS_DEPLOYMENT,
  IMPLEMENTATION,
  INTEGRATION,
  ENABLEMENT,
];
